import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Globe,
  MessageSquare,
  Facebook,
  Instagram,
  Linkedin,
  Search,
  Upload,
  Plus,
  CheckCircle,
  AlertCircle,
  Clock,
  RefreshCw,
  Download,
  Eye,
  Edit,
  Trash2,
  Link,
  Share2,
  Zap
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import { toast } from 'react-hot-toast';
import linkedInService from '../../../api/crm/linkedIn/linkedInService';
import metaService from '../../../api/crm/meta/metaService';
import leadService from '../../../api/crm/leads/leadService';
import { formatDate } from '../../../utils/stringUtils';
import PermissionGate from '../../../components/common/PermissionGate';

// Icon mapping
const iconMap = {
  'Globe': Globe,
  'MessageSquare': MessageSquare,
  'Facebook': Facebook,
  'Linkedin': Linkedin,
  'Search': Search
};

const LeadSourcesCapture = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showConnectorModal, setShowConnectorModal] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [connectors, setConnectors] = useState([]);

  // Fetch all lead sources data
  const fetchAllSourcesData = async () => {
    try {
      setIsLoadingStatus(true);

      // Fetch lead sources data from API
      const sourcesResult = await leadService.getLeadSourcesData();

      if (sourcesResult.success && sourcesResult.sources) {
        // Map API data to connector format
        const mappedConnectors = sourcesResult.sources.map((source, index) => {
          // Format last sync date using HRMS format
          let formattedLastSync = 'Never';
          if (source.lastSyncTimestamp) {
            formattedLastSync = formatDate(source.lastSyncTimestamp, { includeTime: true });
          }

          return {
            id: index + 1,
            name: source.name,
            type: source.type,
            icon: iconMap[source.icon] || Globe,
            status: source.status || 'disconnected',
            lastSync: formattedLastSync,
            leadsCount: source.leadsCount || 0,
            description: source.description || '',
            color: source.color || 'bg-gray-500'
          };
        });

        setConnectors(mappedConnectors);
      } else {
        toast.error('Failed to fetch lead sources data');
        // Keep empty array or show error
      }
    } catch (error) {

    } finally {
      setIsLoadingStatus(false);
    }
  };

  // Refresh sources data after sync
  const refreshSourcesData = async () => {
    await fetchAllSourcesData();
  };

  // Handle OAuth callback from LinkedIn
  const handleOAuthCallback = async () => {
    try {


      toast.loading('Completing LinkedIn authorization...', { id: 'linkedin-callback' });

      // Backend has already exchanged the code for token and stored it

      await refreshSourcesData();


      // Clean up stored state
      const storedState = sessionStorage.getItem('linkedin_oauth_state');
      if (storedState) {
        sessionStorage.removeItem('linkedin_oauth_state');

      }


      toast.success('LinkedIn connected successfully! You can now sync leads.', {
        id: 'linkedin-callback',
        duration: 5000
      });

      // Clean up URL

      navigate('/lead_sources_capture', { replace: true });
    } catch (error) {

      toast.error(error.message || 'Failed to complete LinkedIn authorization', {
        id: 'linkedin-callback',
        duration: 6000
      });
      navigate('/lead_sources_capture', { replace: true });
    }
  };

  // Fetch all lead sources data on component mount
  useEffect(() => {
    fetchAllSourcesData();
  }, []);

  // Handle OAuth callbacks from LinkedIn and Meta
  useEffect(() => {
    const linkedinSuccess = searchParams.get('linkedin_success');
    const linkedinError = searchParams.get('linkedin_error');
    const metaSuccess = searchParams.get('meta_success');
    const metaError = searchParams.get('meta_error');

    if (linkedinSuccess === 'true') {
      handleOAuthCallback();
    } else if (linkedinError) {
      toast.error(`LinkedIn authorization failed: ${decodeURIComponent(linkedinError)}`, { duration: 6000 });
      navigate('/lead_sources_capture', { replace: true });
    }

    if (metaSuccess === 'true') {
      toast.success('Facebook/Instagram connected successfully!', { duration: 5000 });
      refreshSourcesData();
      navigate('/lead_sources_capture', { replace: true });
    } else if (metaError) {
      toast.error(`Meta authorization failed: ${decodeURIComponent(metaError)}`, { duration: 6000 });
      navigate('/lead_sources_capture', { replace: true });
    }
  }, [searchParams, navigate]);


  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'bg-green-100 text-green-800';
      case 'disconnected': return 'bg-gray-100 text-gray-800';
      case 'error': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected': return <CheckCircle className="h-4 w-4" />;
      case 'disconnected': return <Clock className="h-4 w-4" />;
      case 'error': return <AlertCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const handleConnectConnector = async (connector) => {
    // Special handling for LinkedIn - start OAuth flow
    if (connector.type === 'linkedin') {
      try {


        // CRITICAL: Check if already connected with valid token
        const statusResult = await linkedInService.getIntegrationStatus();



        if (statusResult.success && statusResult.connected && statusResult.tokenValid) {

          toast.error('LinkedIn is already connected with a valid token. Use Reconnect if you need to re-authorize.', {
            id: 'linkedin-connect',
            duration: 5000
          });
          return;
        }


        toast.loading('Connecting to LinkedIn...', { id: 'linkedin-connect' });

        // Get authorization URL from backend
        // Backend will check for existing valid token and reject if found
        const result = await linkedInService.getAuthorizationUrl();



        if (result.success && result.authorizationUrl) {
          // Store state in sessionStorage for verification
          if (result.state) {
            sessionStorage.setItem('linkedin_oauth_state', result.state);

          }



          // Redirect user to LinkedIn OAuth page
          window.location.href = result.authorizationUrl;
        } else {

          toast.error(result.error || 'Failed to get LinkedIn authorization URL', { id: 'linkedin-connect' });
        }
      } catch (error) {

        const errorMsg = error.response?.data?.responseMessage || error.message || 'Failed to connect to LinkedIn';
        toast.error(errorMsg, { id: 'linkedin-connect' });
      }
    } else if (connector.type === 'facebook' || connector.type === 'instagram') {
      try {
        toast.loading(`Connecting to ${connector.name}...`, { id: 'meta-connect' });

        const result = await metaService.getAuthorizationUrl();

        if (result.success && result.authorizationUrl) {
          window.location.href = result.authorizationUrl;
        } else {
          toast.error(result.error || `Failed to get ${connector.name} authorization URL`, { id: 'meta-connect' });
        }
      } catch (error) {

        toast.error('Failed to initiate connection', { id: 'meta-connect' });
      }
    } else {
      // For other connectors, show modal
      setSelectedConnector(connector);
      setShowConnectorModal(true);
    }
  };

  const handleManualEntry = () => {
    navigate('/manual_lead_entry');
  };

  const handleViewConnector = (connectorId) => {
    const connector = connectors.find(c => c.id === connectorId);
    if (connector) {
      // Navigate to connector details page
      navigate(`/connector_details/${connectorId}`);
    }
  };

  const handleEditConnector = (connectorId) => {
    const connector = connectors.find(c => c.id === connectorId);
    if (connector) {
      setSelectedConnector(connector);
      setShowConnectorModal(true);
    }
  };

  const handleSyncConnector = async (connectorId) => {
    const connector = connectors.find(c => c.id === connectorId);
    if (connector) {
      try {
        // Special handling for LinkedIn
        if (connector.type === 'linkedin') {
          // Show loading toast with longer duration to ensure it stays visible
          toast.loading('Syncing LinkedIn leads...', {
            id: 'linkedin-sync',
            duration: Infinity // Keep loading toast visible until replaced
          });

          try {
            // CRITICAL: Sync uses stored token, does NOT require accessToken parameter
            // Backend will fetch token from LinkedInIntegration model
            const result = await linkedInService.syncLeads();

            if (result.success) {
              // Refresh all sources data to get updated information
              await refreshSourcesData();

              // Replace loading toast with success toast - only show new leads count
              const newLeadsCount = result.synced || 0;
              toast.success(
                `LinkedIn sync completed: ${newLeadsCount} new lead${newLeadsCount !== 1 ? 's' : ''} captured`,
                { id: 'linkedin-sync' }
              );
            } else {
              // Replace loading toast with error toast
              toast.error(result.error || 'Failed to sync LinkedIn leads', { id: 'linkedin-sync' });
            }
          } catch (syncError) {

            const errorMsg = syncError.response?.data?.responseMessage || syncError.message || 'Failed to sync LinkedIn leads';
            toast.error(errorMsg, { id: 'linkedin-sync' });
          }
        } else if (connector.type === 'facebook' || connector.type === 'instagram') {
          toast.loading(`Syncing ${connector.name} leads...`, { id: 'meta-sync' });

          try {
            const result = await metaService.syncLeads();

            if (result.success) {
              await refreshSourcesData();
              toast.success(`${connector.name} sync complete! ${result.inserted || 0} leads captured.`, { id: 'meta-sync' });
            } else {
              toast.error(result.error || `Failed to sync ${connector.name} leads`, { id: 'meta-sync' });
            }
          } catch (error) {

            toast.error('Sync failed', { id: 'meta-sync' });
          }
        } else if (connector.type === 'website') {
          // Handle website external lead sync
          // Display started message immediately and don't wait for response
          toast.success('Lead sync has been started', {
            id: 'website-sync',
            duration: 4000
          });

          // Trigger sync in background
          leadService.syncExternalLeads()
            .then(async (result) => {
              if (result.success) {
                // process success silently or update UI data
                await refreshSourcesData();

              } else {

              }
            })
            .catch(error => {

            });
        } else {
          // TODO: Implement sync for other connectors
          toast.success(`${connector.name} sync completed successfully.`);
        }
      } catch (error) {

        const errorMsg = error.response?.data?.responseMessage || error.message || 'Unable to sync connector';
        toast.error(errorMsg, {
          icon: '⚠️',
          style: { background: '#f59e0b', color: '#fff' }
        });
      }
    }
  };

  const handleDeleteConnector = async (connectorId) => {
    const connector = connectors.find(c => c.id === connectorId);
    if (connector && window.confirm(`Are you sure you want to delete the ${connector.name} connector? This action cannot be undone.`)) {
      try {
        // await connectorService.deleteConnector(connectorId);
        toast.success(`${connector.name} connector has been deleted successfully.`);
      } catch (error) {
        toast('Unable to delete connector. Please try again.', {
          icon: '⚠️',
          style: { background: '#f59e0b', color: '#fff' }
        });
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="bg-slate-900 p-2.5 rounded-xl shadow-sm border border-slate-800 flex items-center justify-center">
            <Share2 className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">Lead Sources & Capture</h1>
            <p className="text-sm sm:text-base text-slate-500 mt-1">Configure and monitor lead capture from multiple channels</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
          <PermissionGate permission="lead_management" action="create">
            <Button
              variant="custom"
              size="sm"
              icon={<Plus className="h-4 w-4" />}
              onClick={handleManualEntry}
              className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md"
            >
              Add Lead
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Connectors Grid - Direct page without tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="space-y-4 sm:y-6">
          {/* Loading State */}
          {isLoadingStatus && connectors.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
                <p className="text-gray-600">Loading lead sources...</p>
              </div>
            </div>
          ) : connectors.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No lead sources found.</p>
            </div>
          ) : (
            /* Connectors Grid */
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-4 sm:gap-6">
              {connectors.map((connector) => {
                const IconComponent = connector.icon;
                return (
                  <div key={connector.id} className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 md:p-6 hover:shadow-md transition-shadow">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3 sm:mb-4 space-y-2 sm:space-y-0">
                      <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                        <div className={`p-1.5 sm:p-2 rounded-lg ${connector.color} flex-shrink-0`}>
                          <IconComponent className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 truncate" title={connector.name}>{connector.name}</h3>
                          <p className="text-xs sm:text-sm text-gray-500 truncate mt-1" title={connector.description}>{connector.description}</p>
                        </div>
                      </div>
                      <span className={`inline-flex items-center px-2 py-0.5 sm:px-2.5 rounded-full text-xs font-medium ${getStatusColor(connector.status)} flex-shrink-0 self-start sm:self-auto`}>
                        {getStatusIcon(connector.status)}
                        <span className="ml-1 capitalize hidden md:inline">{connector.status}</span>
                      </span>
                    </div>

                    <div className="space-y-2 mb-3 sm:mb-4">
                      <div className="flex items-center justify-between text-xs sm:text-sm">
                        <span className="text-gray-500">Leads in Database:</span>
                        <span className="font-medium text-gray-900">{connector.leadsCount}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs sm:text-sm">
                        <span className="text-gray-500">Last Sync:</span>
                        <span className="font-medium text-gray-900 truncate" title={connector.lastSync}>{connector.lastSync}</span>
                      </div>
                    </div>

                    <div className="flex flex-col md:flex-row items-stretch md:items-center space-y-2 md:space-y-0 md:space-x-2">
                      {connector.status === 'connected' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          icon={<RefreshCw className="h-3 w-3 sm:h-4 sm:w-4" />}
                          onClick={() => handleSyncConnector(connector.id)}
                          className="w-full md:w-auto text-xs sm:text-sm justify-center md:justify-start"
                        >
                          Sync
                        </Button>
                      ) : (
                        <Button
                          variant="custom"
                          size="sm"
                          icon={<Link className="h-3 w-3 sm:h-4 sm:w-4" />}
                          onClick={() => handleConnectConnector(connector)}
                          className="w-full md:w-auto bg-slate-900 hover:bg-slate-800 text-white text-xs sm:text-sm justify-center md:justify-start rounded-md shadow-sm transition-colors"
                        >
                          Connect
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Connector Configuration Modal */}
      {showConnectorModal && selectedConnector && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowConnectorModal(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${selectedConnector.color} sm:mx-0 sm:h-10 sm:w-10`}>
                    {React.createElement(selectedConnector.icon, { className: "h-6 w-6 text-white" })}
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Connect {selectedConnector.name}
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        {selectedConnector.type === 'website' 
                          ? 'This will verify the connection to your external lead database configured in the environment variables.'
                          : `Configure settings for ${selectedConnector.name} integration.`}
                      </p>
                      {selectedConnector.type === 'website' && (
                        <div className="mt-4 p-3 bg-blue-50 rounded-md border border-blue-100">
                          <div className="flex">
                            <AlertCircle className="h-5 w-5 text-blue-400" />
                            <div className="ml-3">
                              <h3 className="text-sm font-medium text-blue-800">Configuration Info</h3>
                              <div className="mt-2 text-xs text-[#0B2033]">
                                <p>System will use <strong>LEAD_MONGODB_URI</strong> from server configuration.</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <Button
                  variant="primary"
                  onClick={async () => {
                    toast.loading(`Connecting to ${selectedConnector.name}...`, { id: 'connector-setup' });
                    try {
                      // Trigger a sync which also serves as a connection test
                      const result = await leadService.syncExternalLeads();
                      if (result.success) {
                        toast.success(`${selectedConnector.name} connected and synced successfully!`, { id: 'connector-setup' });
                        await refreshSourcesData();
                        setShowConnectorModal(false);
                      } else {
                        toast.error(result.error || 'Failed to connect', { id: 'connector-setup' });
                      }
                    } catch (err) {
                      toast.error('Connection failed', { id: 'connector-setup' });
                    }
                  }}
                  className="w-full sm:w-auto sm:ml-3 bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                >
                  Verify & Connect
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowConnectorModal(false)}
                  className="mt-3 w-full sm:w-auto sm:mt-0"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadSourcesCapture;
